import { isStoryItem, STORY_STAFF, storyItems } from '../stories/words';
import data from './course.generated.json';
import type { Course, Goal, Item, Lesson, Staff, Track, Unit } from './types';

export const course = data as Course;

/** Every phrase that can have cards: the course's and those kept from the stories. */
export const allItems: Item[] = [...course.items, ...storyItems];
/** The course as reviews see it: its lessons, and every phrase that can be reviewed. */
export const reviewCourse: Course = { ...course, items: allItems };

const itemsById = new Map(allItems.map((i) => [i.id, i]));
const lessonsById = new Map(course.lessons.map((l) => [l.id, l]));

export function getItem(id: string): Item {
  const item = itemsById.get(id);
  if (!item) throw new Error(`Unknown phrase ${id}`);
  return item;
}

export function getLesson(id: string): Lesson {
  const lesson = lessonsById.get(id);
  if (!lesson) throw new Error(`Unknown lesson ${id}`);
  return lesson;
}

export function frameOf(item: Item) {
  return item.slot ? course.frames.find((f) => f.id === item.slot!.frame) ?? null : null;
}

/** The learner's track; before a goal is chosen, the Tourist track (the shared start of both). */
export function trackFor(goal: Goal | null): Track {
  return course.tracks.find((t) => t.id === (goal ?? 'tourist')) ?? course.tracks[0];
}

/** A track's lessons in its own order (a lesson shared by two units is listed once). */
export function trackLessons(track: Track): Lesson[] {
  return [...new Set(track.units.flatMap((u) => u.lessons))].map(getLesson);
}

/** How many lessons every track starts with in common: a learner can choose a goal after them. */
export function sharedStart(): number {
  const paths = course.tracks.map((t) => trackLessons(t).map((l) => l.id));
  let n = 0;
  while (paths.every((p) => p[n] !== undefined && p[n] === paths[0][n])) n++;
  return n;
}

/** A track's phrases in the order they are taught. */
export function trackItems(track: Track): Item[] {
  return trackLessons(track).flatMap((l) => l.phrases.map(getItem));
}

/** Who the learner talks to in a phrase's lesson. */
export function staffOf(item: Item): Staff {
  if (isStoryItem(item.id)) return STORY_STAFF;
  return getLesson(item.lesson).staff;
}

/** A role inside a sentence: "the waiter", "the ΚΕΠ clerk" (an acronym or a Greek word keeps its letters). */
export function roleName(staff: Staff): string {
  const first = staff.role.split(' ')[0];
  if (first.length > 1 && first === first.toUpperCase()) return staff.role;
  return staff.role.charAt(0).toLowerCase() + staff.role.slice(1);
}

/** The unit of a track that holds a lesson, with its position. */
export function unitOf(track: Track, lessonId: string): { unit: Unit; index: number } | null {
  const index = track.units.findIndex((u) => u.lessons.includes(lessonId));
  return index < 0 ? null : { unit: track.units[index], index };
}

export type { Course, Gender, Goal, Item, Lesson, Staff, Track, Unit } from './types';
